// src/screens/pass/StripePaymentModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Group, Text } from "@mantine/core";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { getCurrentStripePK } from "../../atoms/passNetwork";

function CheckoutInner({ onClose, onPaid, amount, currency = "aud" }) {
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [walletDiag, setWalletDiag] = useState("");

  // 🔎 Diagnose wallet availability (Google Pay / Apple Pay)
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!stripe) return;
      if (!amount || !Number.isFinite(Number(amount))) return;

      try {
        const pr = stripe.paymentRequest({
          country: "AU",
          currency,
          total: {
            label: "Total",
            amount: Math.round(Number(amount) * 100),
          },
          requestPayerName: true,
          requestPayerEmail: false,
          requestPayerPhone: true,
        });

        const result = await pr.canMakePayment();
        if (cancelled) return;

        // result is null OR {googlePay:true} OR {applePay:true} OR both
        setWalletDiag(JSON.stringify(result));
      } catch (e) {
        if (cancelled) return;
        setWalletDiag(`canMakePayment error: ${e?.message || String(e)}`);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [stripe, amount, currency]);

  async function onPay() {
    if (!stripe || !elements) return;

    setSubmitting(true);
    setErr("");

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (error) {
      setErr(error.message || "Payment failed");
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      onPaid();
      return;
    }

    setErr(`Payment status: ${paymentIntent?.status || "unknown"}`);
    setSubmitting(false);
  }

  return (
    <>
      <PaymentElement />
      {err ? (
        <Text c="red" size="sm" mt="sm">
          {err}
        </Text>
      ) : null}

      <Group mt="md" justify="space-between">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={onPay} disabled={!stripe} loading={submitting}>
          Pay
        </Button>
      </Group>
    </>
  );
}

export default function StripePayModal({
  opened,
  onClose,
  clientSecret,
  onPaid,
  publishableKey,
  amount,
  currency = "aud",
}) {
  const pk = publishableKey || getCurrentStripePK();

  const stripePromise = useMemo(() => {
    if (!pk) return null;
    if (!/^pk_(test|live)_/.test(pk)) return null;
    return loadStripe(pk);
  }, [pk]);

  const missing = !clientSecret || !stripePromise;

  // ✅ IMPORTANT: explicitly allow wallets (Google Pay + Apple Pay)
  const elementsOptions = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      // This is the key part that ensures wallets are enabled in PaymentElement
      wallets: {
        applePay: "auto",
        googlePay: "auto",
      },
    };
  }, [clientSecret]);

  return (
    <Modal opened={opened} onClose={onClose} title="Complete payment" centered>
      {missing || !elementsOptions ? (
        <Text c="red" size="sm">
          Payment system not ready yet. Please go back and reopen the passes link.
        </Text>
      ) : (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <CheckoutInner
            onClose={onClose}
            onPaid={onPaid}
            amount={amount}
            currency={currency}
          />
        </Elements>
      )}
    </Modal>
  );
}
